<template>
  <div class="login-page min-h-screen bg-bochas-bg px-4 py-6 sm:px-6 lg:px-8">
    <div
      class="mx-auto grid min-h-[calc(100vh-3rem)] max-w-7xl items-center gap-8 lg:grid-cols-[0.9fr_1fr]"
    >
      <section class="login-brand hidden lg:block">
        <router-link
          to="/"
          class="inline-flex items-center gap-2 text-sm font-semibold text-bochas-text-muted transition-colors hover:text-white"
        >
          <IconFbochas name="chevron-left" :size="18" />
          Volver al sitio
        </router-link>

        <div class="login-brand-panel mt-10">
          <div class="login-brand-mark" aria-hidden="true"></div>
          <div class="relative">
            <img
              src="@/assets/images/logoFederacion.png"
              alt="Federación de Bochas Córdoba"
              class="h-24 w-24 object-contain"
            />
            <p class="mt-8 text-xs font-semibold uppercase text-bochas-rojo">
              Acceso interno
            </p>
            <h1
              class="mt-2 max-w-sm text-4xl font-bold leading-tight text-white"
            >
              Panel de administración
            </h1>
            <p class="mt-3 text-sm font-semibold text-bochas-text-muted">
              Federación de Bochas Córdoba
            </p>
            <div class="mt-8 flex flex-wrap gap-2">
              <span class="login-chip">
                <IconFbochas name="shield" :size="16" />
                Acceso seguro
              </span>
              <span class="login-chip">
                <IconFbochas name="users" :size="16" />
                Gestión interna
              </span>
            </div>
          </div>
        </div>
      </section>

      <section class="login-card">
        <div class="login-mobile-brand mb-6 lg:hidden">
          <router-link
            to="/"
            class="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/[0.06] text-bochas-text-muted transition-colors hover:text-white"
            aria-label="Volver al sitio"
          >
            <IconFbochas name="chevron-left" :size="18" />
          </router-link>
          <img
            src="@/assets/images/logoFederacion.png"
            alt="Federación de Bochas Córdoba"
            class="h-12 w-12 object-contain"
          />
          <div class="min-w-0 flex-1 text-right">
            <p class="truncate text-sm font-bold text-white">
              Federación de Bochas Córdoba
            </p>
            <p class="mt-0.5 text-xs font-semibold uppercase text-bochas-rojo">
              Acceso interno
            </p>
          </div>
        </div>

        <div class="mb-7">
          <p class="text-sm font-semibold text-bochas-rojo">Iniciar sesión</p>
          <h2 class="mt-2 text-2xl font-bold text-white">Entrar al panel</h2>
        </div>

        <form @submit.prevent="onSubmit" class="space-y-5">
          <div>
            <label for="email" class="block text-sm font-medium text-gray-300">
              Email
            </label>
            <input
              id="email"
              ref="emailInput"
              v-model="email"
              type="email"
              autocomplete="email"
              required
              class="login-input mt-2"
              placeholder="admin@bochascba.com"
            />
          </div>

          <div>
            <label
              for="password"
              class="block text-sm font-medium text-gray-300"
            >
              Contraseña
            </label>
            <div class="relative mt-2">
              <input
                id="password"
                ref="passwordInput"
                v-model="password"
                :type="showPassword ? 'text' : 'password'"
                autocomplete="current-password"
                required
                class="login-input pr-12"
                placeholder="••••••••"
                @keyup.enter="onSubmit"
              />
              <button
                type="button"
                class="login-password-toggle"
                :aria-label="
                  showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'
                "
                :title="
                  showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'
                "
                @click="togglePassword"
              >
                <IconFbochas
                  :name="showPassword ? 'eye-off' : 'eye'"
                  :size="18"
                />
              </button>
            </div>
          </div>

          <div
            v-if="error"
            class="rounded-lg border border-bochas-rojo bg-bochas-rojo/20 px-4 py-3 text-sm text-red-100"
          >
            {{ error }}
          </div>

          <button
            type="submit"
            :disabled="loading"
            class="btn-bochas-rojo inline-flex w-full items-center justify-center gap-2"
            :class="{ 'cursor-not-allowed opacity-60': loading }"
          >
            <span
              v-if="loading"
              class="h-4 w-4 rounded-full border-2 border-white/50 border-t-white animate-spin"
              aria-hidden="true"
            ></span>
            <span>{{ loading ? "Ingresando..." : "Ingresar" }}</span>
          </button>
        </form>

        <div v-if="needsSetup" class="mt-6 border-t border-white/10 pt-5 text-center">
          <router-link
            to="/admin/setup"
            class="text-xs font-semibold text-bochas-text-muted transition-colors hover:text-white"
          >
            Primera vez? Crear administrador
          </router-link>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import IconFbochas from "@/components/Icons/IconFbochas.vue";
import { getSetupStatus, signIn } from "@/services/auth/authService";

const router = useRouter();
const email = ref("");
const password = ref("");
const error = ref("");
const loading = ref(false);
const emailInput = ref(null);
const passwordInput = ref(null);
const showPassword = ref(false);
const needsSetup = ref(false);

async function onSubmit() {
  error.value = "";
  loading.value = true;
  try {
    await signIn(email.value, password.value);
    router.push("/admin");
  } catch (err) {
    error.value =
      err.response?.data?.message ||
      "No pudimos iniciar sesión. Intentá nuevamente en unos segundos.";
  } finally {
    loading.value = false;
  }
}

function togglePassword() {
  showPassword.value = !showPassword.value;
}

onMounted(async () => {
  emailInput.value?.focus();
  try {
    const status = await getSetupStatus();
    needsSetup.value = status.needsSetup === true;
  } catch (_) {
    needsSetup.value = false;
  }
});
</script>

<style scoped>
.login-page {
  background: radial-gradient(
      circle at 18% 18%,
      rgba(165, 33, 33, 0.16),
      transparent 28rem
    ),
    radial-gradient(
      circle at 82% 76%,
      rgba(29, 82, 124, 0.16),
      transparent 30rem
    ),
    var(--bochas-bg);
}

.login-brand,
.login-card {
  position: relative;
}

.login-brand-panel {
  position: relative;
  overflow: hidden;
  min-height: 27rem;
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 1rem;
  background: linear-gradient(135deg, rgba(165, 33, 33, 0.2), transparent 38%),
    linear-gradient(315deg, rgba(29, 82, 124, 0.2), transparent 44%),
    rgba(26, 31, 46, 0.78);
  padding: 2rem;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.08),
    0 1.5rem 4rem rgba(0, 0, 0, 0.24);
}

.login-brand-mark {
  position: absolute;
  inset: auto -5rem -6rem auto;
  width: 19rem;
  height: 19rem;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 9999px;
  background: radial-gradient(
      circle,
      transparent 0 34%,
      rgba(255, 255, 255, 0.05) 35% 36%,
      transparent 37% 55%,
      rgba(255, 255, 255, 0.06) 56% 57%,
      transparent 58%
    ),
    linear-gradient(135deg, rgba(255, 255, 255, 0.08), transparent);
}

.login-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.06);
  padding: 0.55rem 0.75rem;
  color: rgba(255, 255, 255, 0.78);
  font-size: 0.78rem;
  font-weight: 800;
}

.login-card {
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.75rem;
  background: rgba(26, 31, 46, 0.92);
  box-shadow: 0 1.5rem 4rem rgba(0, 0, 0, 0.28);
  padding: 1.5rem;
  width: 100%;
}

.login-mobile-brand {
  display: grid;
  grid-template-columns: 2.5rem 3rem minmax(0, 1fr);
  align-items: center;
  gap: 0.75rem;
  margin: -0.4rem -0.4rem 1.5rem;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 0.75rem;
  background: linear-gradient(135deg, rgba(165, 33, 33, 0.14), transparent 44%),
    rgba(255, 255, 255, 0.04);
  padding: 0.75rem;
}

.login-input {
  width: 100%;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 0.75rem;
  background: rgba(10, 14, 26, 0.72);
  padding: 0.85rem 1rem;
  color: white;
  outline: none;
  transition: border-color 180ms ease, box-shadow 180ms ease,
    background-color 180ms ease;
}

.login-input::placeholder {
  color: rgba(255, 255, 255, 0.34);
}

.login-input:focus {
  border-color: rgba(165, 33, 33, 0.85);
  background: rgba(10, 14, 26, 0.96);
  box-shadow: 0 0 0 3px rgba(165, 33, 33, 0.18);
}

.login-password-toggle {
  position: absolute;
  right: 0.55rem;
  top: 50%;
  display: inline-flex;
  height: 2.25rem;
  width: 2.25rem;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  color: rgba(255, 255, 255, 0.62);
  transform: translateY(-50%);
  transition: background-color 180ms ease, color 180ms ease;
}

.login-password-toggle:hover {
  background: rgba(255, 255, 255, 0.08);
  color: white;
}

@media (min-width: 640px) {
  .login-card {
    padding: 2rem;
  }
}

@media (min-width: 1024px) {
  .login-card {
    max-width: 27rem;
    justify-self: end;
  }
}
</style>
